import mapboxgl from 'mapbox-gl';

export const MAPBOX_TOKEN = 'pk.eyJ1Ijoic3Rvcm1ieXRlLWRldiIsImEiOiJjbThxczFyb2owcHpjMmtvZDZ6eG9paTJoIn0.TwaA5xrxn3fwmJaks2c07Q';

/**
 * Applies consistent map styling across all map instances
 * Matches mobile app styling with terrain, atmosphere, and 3D buildings
 */
export function applyMapStyling(map: mapboxgl.Map): void {
  const apply = () => {
    // Remove starry atmosphere/fog and set white space color
    map.setFog({
      'space-color': '#ffffff',
      'star-intensity': 0,
      'horizon-blend': 0
    });

    // Add DEM source for terrain - match mobile app settings
    if (!map.getSource('mapbox-dem')) {
      map.addSource('mapbox-dem', {
        type: 'raster-dem',
        url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
        tileSize: 514, // Match mobile app
        maxzoom: 14
      });
    }

    // Add terrain layer - match mobile app exaggeration
    map.setTerrain({ source: 'mapbox-dem', exaggeration: 3.0 });

    // Hide country and continent labels, reduce other labels
    const hiddenLabelLayers = [
      'country-label',
      'state-label',
      'natural-label',
      'natural-point-label',
      'natural-line-label',
      'water-label',
      'water-point-label',
      'water-line-label',
      'waterway-label'
    ];
    const dimmedLabelLayers = [
      'settlement-subdivision-label',
      'settlement-label',
      'poi-label',
      'transit-label',
      'road-label'
    ];

    hiddenLabelLayers.forEach(layerId => {
      if (map.getLayer(layerId)) {
        map.setPaintProperty(layerId, 'text-opacity', 0);
        if (map.getPaintProperty(layerId, 'icon-opacity') !== undefined) {
          map.setPaintProperty(layerId, 'icon-opacity', 0);
        }
      }
    });

    dimmedLabelLayers.forEach(layerId => {
      if (map.getLayer(layerId)) {
        map.setPaintProperty(layerId, 'text-opacity', 0.3);
        if (map.getPaintProperty(layerId, 'icon-opacity') !== undefined) {
          map.setPaintProperty(layerId, 'icon-opacity', 0.3);
        }
      }
    });

    // Hide country boundaries
    const boundaryLayers = [
      'admin-0-boundary',
      'admin-1-boundary',
      'admin-0-boundary-disputed',
      'admin-0-boundary-bg'
    ];

    boundaryLayers.forEach(layerId => {
      if (map.getLayer(layerId)) {
        map.setPaintProperty(layerId, 'line-opacity', 0);
      }
    });

    // Add 3D buildings like mobile app
    if (!map.getLayer('3d-buildings')) {
      map.addLayer({
        id: '3d-buildings',
        source: 'composite',
        'source-layer': 'building',
        filter: ['==', 'extrude', 'true'],
        type: 'fill-extrusion',
        minzoom: 13,
        paint: {
          'fill-extrusion-color': '#aaa',
          'fill-extrusion-opacity': 0.7,
          'fill-extrusion-height': ['get', 'height'],
          'fill-extrusion-base': ['get', 'min_height']
        }
      });
    }
  };

  if (map.isStyleLoaded()) {
    apply();
  }
  map.on('style.load', apply);
}

/**
 * Creates a map instance with consistent configuration
 */
export function createStyledMap(
  container: HTMLDivElement,
  options: Omit<mapboxgl.MapboxOptions, 'container' | 'style' | 'projection' | 'attributionControl' | 'logoPosition'>
): mapboxgl.Map {
  mapboxgl.accessToken = MAPBOX_TOKEN;

  const map = new mapboxgl.Map({
    container,
    style: 'mapbox://styles/mapbox/outdoors-v12',
    projection: 'globe',
    attributionControl: false,
    logoPosition: 'bottom-left',
    renderWorldCopies: false,
    ...options,
  });

  applyMapStyling(map);

  return map;
}
